import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Loader2 } from "lucide-react";
import { ISSUE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = ({ dark = false, compact = false, idPrefix = "quote" }) => {
  const { pathname } = useLocation();
  const [form, setForm] = useState({ name: "", phone: "", issue: "", urgent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.issue) {
      toast.error("Please fill in your name, phone, and issue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source_page: pathname });
      setDone(true);
      toast.success("Request received — we'll call you back shortly!");
    } catch {
      toast.error("Something went wrong. Please call us at (949) 371-5993.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark
    ? "w-full rounded-md bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:border-[#FF6500] transition-colors"
    : "w-full rounded-md bg-white border border-gray-200 px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#FF6500] transition-colors";

  if (done) {
    return (
      <div data-testid={`${idPrefix}-form-success`} className={`rounded-xl p-8 text-center ${dark ? "bg-white/10 text-white" : "bg-orange-50 text-gray-900"}`}>
        <Flame className="w-10 h-10 text-[#FF6500] mx-auto mb-3" />
        <p className="font-heading font-bold text-xl mb-1">Request Received</p>
        <p className={dark ? "text-white/70" : "text-gray-600"}>
          A water heater specialist will call you back within minutes during business hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className={compact ? "grid gap-3 sm:grid-cols-2" : "grid gap-3"}>
      <input
        data-testid={`${idPrefix}-name-input`}
        className={inputCls}
        placeholder="Your name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <input
        data-testid={`${idPrefix}-phone-input`}
        className={inputCls}
        placeholder="Phone number"
        type="tel"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
      />
      <select
        data-testid={`${idPrefix}-issue-select`}
        className={`${inputCls} ${!form.issue ? (dark ? "text-white/50" : "text-gray-400") : ""} ${compact ? "sm:col-span-2" : ""}`}
        value={form.issue}
        onChange={(e) => setForm({ ...form, issue: e.target.value })}
      >
        <option value="" disabled>
          What's going on with your water heater?
        </option>
        {ISSUE_OPTIONS.map((o) => (
          <option key={o} value={o} className="text-gray-900">
            {o}
          </option>
        ))}
      </select>
      <label
        data-testid={`${idPrefix}-urgent-toggle`}
        className={`flex items-center gap-3 cursor-pointer select-none ${compact ? "sm:col-span-2" : ""} ${dark ? "text-white/80" : "text-gray-700"}`}
      >
        <input
          type="checkbox"
          checked={form.urgent}
          onChange={(e) => setForm({ ...form, urgent: e.target.checked })}
          className="w-5 h-5 accent-[#FF6500]"
        />
        <span className="text-sm font-medium">This is urgent — no hot water / active leak</span>
      </label>
      <button
        data-testid={`${idPrefix}-submit-btn`}
        type="submit"
        disabled={loading}
        className={`${compact ? "sm:col-span-2" : ""} rounded-md bg-[#FF6500] text-white font-heading font-bold text-lg py-4 hover:bg-[#E85C0D] hover:scale-[1.02] active:scale-[0.99] transition-[background-color,transform] flex items-center justify-center gap-2 disabled:opacity-60`}
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Flame className="w-5 h-5" />}
        Get My Free Estimate
      </button>
      <p className={`text-xs ${compact ? "sm:col-span-2" : ""} ${dark ? "text-white/50" : "text-gray-400"}`}>
        No spam, no obligation. We call back within minutes during business hours.
      </p>
    </form>
  );
};
